import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import { apiClient } from "../../../zodios/client";
import { type AddTargetsToAdUpdate } from "../../../zodios/apiDefinitions/api/ads";
import { toast } from "react-toastify";
import { errorToast, successToast } from "../../../utils/toasts";
import { toTitleCase } from "../../../utils/formatText";
import type { TSelector } from "../../../i18n/types";

// Skip codes from `api/views/targets.py`.
const SKIP_MESSAGES: Record<string, TSelector> = {
  auto_campaign_positive_keyword: ($) =>
    $.advertising["Auto-targeting campaigns choose their own keywords, so manual keywords cannot be added."],
  auto_campaign_positive_product: ($) =>
    $.advertising["Auto-targeting campaigns choose their own products, so manual products cannot be added."],
  walmart_negative_keyword_invalid: ($) =>
    $.advertising["Walmart negative keywords must be under 80 characters and cannot contain > < ! @ % * or ,."],
};

// Where a code this build does not know lands. The server's own `reason` prose is untranslated
// and written for a log, so it belongs in the Sentry issue rather than in front of a user.
const UNSPECIFIED = "unspecified";
const UNSPECIFIED_MESSAGE: TSelector = ($) => $.advertising["They could not be added to this media plan."];

type SkippedTarget = {
  keyword?: string;
  products?: string[];
  code?: string;
};

export const useAddTargetsToAd = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["advertising"]);

  // Grouped by skip code — the stable identity, and the toast's id: the bulk flows post one
  // request per selected row, so one id per refusal is what keeps thirty rows to one toast.
  const explain = (skipped: SkippedTarget[] | undefined) => {
    const groups = new Map<string, { message: string; targets: string[] }>();

    for (const { code, keyword, products } of skipped ?? []) {
      const named = code === undefined ? undefined : SKIP_MESSAGES[code];
      const id = code !== undefined && named !== undefined ? code : UNSPECIFIED;
      const targets = groups.get(id)?.targets ?? [];

      groups.set(id, {
        message: t(named ?? UNSPECIFIED_MESSAGE),
        targets: [...targets, ...(keyword ? [keyword] : []), ...(products ?? [])],
      });
    }

    return [...groups].map(([id, { message, targets }]) => ({
      id,
      message:
        targets.length > 0 ?
          `${message} ${t(($) => $.advertising["Affected targets include: {targets}"], { targets: targets.join(", ") })}`
        : message,
    }));
  };

  // A refusal lists what it skipped; a `ValidationError` 400 (missing or mismatched ad group)
  // carries no such list, so it explains to nothing and stays a fault to report.
  const explainRefusal = (error: unknown) =>
    explain(
      isAxiosError<{ skipped_targets?: SkippedTarget[] } | undefined>(error) && error.response?.status === 400 ?
        error.response.data?.skipped_targets
      : undefined,
    );

  return useMutation({
    // A refusal we could name is a breadcrumb; a code this build does not know means it is behind
    // the server and still owes the user a real sentence, so that stays an issue.
    meta: {
      expectedError: (error: unknown) => {
        const refusals = explainRefusal(error);
        return refusals.length > 0 && refusals.every(({ id }) => id !== UNSPECIFIED);
      },
    },
    mutationFn: async (payload: AddTargetsToAdUpdate) => await apiClient.addTargetsToAd(payload),
    onMutate: (vars: AddTargetsToAdUpdate) => {
      const message =
        vars.data.target_type === "product" ?
          t(($) => $.advertising["Updating Media Plan Targets"])
        : t(($) => $.advertising["Updating Media Plan {type}s"], { type: toTitleCase(vars.data.target_type) });
      toast.info(message, {
        autoClose: 3000,
        toastId: "ad-status-toast",
      });
    },
    onSuccess: (data, variables) => {
      const { target_type } = variables.data;
      const refusals = explain(data.skipped_targets);

      if (refusals.length > 0) {
        // A partly refused submission still answers 200, so success alone would hide the skips.
        refusals.forEach(({ id, message }) =>
          errorToast({
            title: t(($) => $.advertising["Some targets were not added"]),
            message,
            options: { type: "warning", toastId: `add-targets-skipped:${id}` },
          }),
        );
      } else if (data.created_count === 0) {
        // Not managed by Trellis, or every target was already on the ad — nothing created, still 200.
        errorToast({
          title: t(($) => $.advertising["No targets were added"]),
          message: t(
            ($) => $.advertising["They may already be on this media plan, or it may not be managed by Trellis."],
          ),
          options: { type: "warning", toastId: "add-targets-none-created" },
        });
      } else {
        const message =
          target_type === "product" ?
            t(($) => $.advertising["Targets Successfully Added"])
          : t(($) => $.advertising["{type}s Successfully Added"], { type: toTitleCase(target_type) });
        successToast({ message });
      }

      void queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey.includes("keyword_report") ||
          query.queryKey.includes("keyword_report_meta") ||
          query.queryKey.includes("adPlanConfig"),
      });
    },
    onError: (error) => {
      const refusals = explainRefusal(error);
      const title = t(($) => $.advertising["No targets were added"]);

      if (refusals.length === 0) {
        errorToast({
          title,
          message: t(($) => $.advertising["Please try again, or contact support."]),
          error,
          options: { toastId: "add-targets-failed" },
        });
        return;
      }

      refusals.forEach(({ id, message }) =>
        errorToast({ title, message, error, options: { toastId: `add-targets-refused:${id}` } }),
      );
    },
  });
};
